import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
// Force reload to fix import issue
import styled from 'styled-components'
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  FileSpreadsheet,
  Zap,
  Tags,
  Rocket,
  Database,
  Brain,
  Wrench,
  Scale,
  Building2,
  HelpCircle,
  Settings,
  LogOut,
  User,
  Folder,
} from 'lucide-react'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useAuth } from '../../../hooks/useAuth'
import { useFetchUserProfileQuery } from '../../../services/authApi'
import { useGetOrganizationBrandingPublicQuery } from '../../../services/orgsApi'
import { activeBranding as branding } from '../../../config'
import { useAnnotationStats } from '../../../store/hooks'
import ConfirmDialog from '../../modals/ConfirmDialog'
import SelectProjectModal from '../../modals/SelectProjectModal'
import { toTitleCase } from '../../../utils/helpers'
import { Badge } from 'react-bootstrap'

const SidebarContainer = styled.div<{ $isCollapsed: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  background: linear-gradient(180deg, rgba(26, 26, 26, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%);
  height: 100vh;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${(props) => (props.$isCollapsed ? '80px' : '280px')};
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.4), inset -1px 0 0 rgba(255, 255, 255, 0.05);
  border-right: 1px solid rgba(34, 211, 238, 0.18);
  z-index: 1000;
  overflow-x: hidden;
  overflow-y: hidden;
  backdrop-filter: blur(20px);
  /* Isolate layout so internal animations/hover effects don't impact the main content */
  contain: layout style;
  /* No bottom padding here; applied to scroll area to avoid affecting overall layout */

  /* Add CSS class for global layout adjustments */
  &.sidebar-collapsed {
    width: 80px;
  }

  &.sidebar-expanded {
    width: 280px;
  }
`

const ProjectSelector = styled.div<{ $isCollapsed: boolean }>`
  padding: ${(props) => (props.$isCollapsed ? '12px 8px' : '18px 20px')};
  border-bottom: 1px solid rgba(34, 211, 238, 0.12);
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.1) 0%, rgba(20, 184, 166, 0.07) 100%);
  position: relative;
  display: ${(props) => (props.$isCollapsed ? 'flex' : 'block')};
  justify-content: ${(props) => (props.$isCollapsed ? 'center' : 'flex-start')};
  align-items: ${(props) => (props.$isCollapsed ? 'center' : 'stretch')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.35), transparent);
  }

  &:hover {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.16) 0%, rgba(20, 184, 166, 0.1) 100%);
  }

  .project-label {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    overflow: hidden;
    font-weight: 500;
  }

  .project-name {
    color: white;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: linear-gradient(135deg, #ffffff 0%, #22d3ee 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: -0.3px;
  }

  .project-badge {
    margin-left: 8px;
    font-size: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(4px);
  }
`

const SidebarHeader = styled.div<{ $isCollapsed: boolean }>`
  padding: ${(props) => (props.$isCollapsed ? '1.5rem 1rem' : '1.75rem 1.5rem')};
  border-bottom: 1px solid rgba(34, 211, 238, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.06) 0%, transparent 100%);
`

const Logo = styled.img<{ $isCollapsed: boolean }>`
  height: ${(props) => (props.$isCollapsed ? '32px' : '40px')};
  width: auto;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 8px rgba(34, 211, 238, 0.45));
`

const BrandText = styled.div<{ $isCollapsed: boolean }>`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  opacity: ${(props) => (props.$isCollapsed ? '0' : '1')};
  visibility: ${(props) => (props.$isCollapsed ? 'hidden' : 'visible')};
  transition: all 0.3s ease;
  white-space: nowrap;
  text-align: center;
`

//
const SidebarContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  min-height: 0;
  /* Reserve space so bottom user section never overlaps */
  padding-bottom: 72px;
`

const TooltipBubble = styled.div`
  position: fixed;
  left: 88px; /* 80px collapsed width + 8px gap */
  transform: translateY(-50%);
  padding: 4px 8px;
  background: rgba(26, 26, 26, 0.95);
  color: #fff;
  border: 1px solid rgba(34, 211, 238, 0.3);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1;
  z-index: 1100;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  white-space: nowrap;
`

const NavSection = styled.div`
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(34, 211, 238, 0.1);

  &:last-child {
    border-bottom: none;
  }
`

const SectionTitle = styled.div<{ $isCollapsed: boolean }>`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0 1.5rem 0.75rem;
  opacity: ${(props) => (props.$isCollapsed ? '0' : '1')};
  visibility: ${(props) => (props.$isCollapsed ? 'hidden' : 'visible')};
  transition: all 0.3s ease;
  margin-bottom: 0.25rem;
`

const NavItem = styled.button<{
  $isActive: boolean
  $isCollapsed: boolean
  $isDisabled?: boolean
  $isSubItem?: boolean
}>`
  padding: ${(props) =>
    props.$isCollapsed
      ? '0.5rem 0.25rem'
      : props.$isSubItem
      ? '0.625rem 1.5rem 0.625rem 3rem'
      : '0.875rem 1.5rem'};
  margin: ${(props) => (props.$isCollapsed ? '0.25rem 0.75rem' : '0 0.75rem')};
  width: calc(100% - 1.5rem);
  background: ${(props) => (props.$isActive ? 'rgba(34, 211, 238, 0.15)' : 'transparent')};
  border: none;
  border-radius: 8px;
  color: ${(props) =>
    props.$isDisabled
      ? 'rgba(255, 255, 255, 0.3)'
      : props.$isActive
      ? '#ffffff'
      : 'rgba(255, 255, 255, 0.85)'};
  text-align: left;
  cursor: ${(props) => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isCollapsed ? 'center' : 'flex-start')};
  gap: 0.75rem;
  position: relative;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.$isActive ? '600' : '500')};
  opacity: ${(props) => (props.$isDisabled ? 0.5 : 1)};

  &::after {
    content: '';
    position: absolute;
    left: 0.75rem;
    top: 0;
    height: 100%;
    width: ${(props) => (props.$isActive && !props.$isCollapsed ? '3px' : '0')};
    background: linear-gradient(180deg, #22d3ee 0%, #14b8a6 100%);
    border-radius: 0 3px 3px 0;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    background: ${(props) =>
      !props.$isDisabled && !props.$isActive
        ? 'linear-gradient(90deg, rgba(34, 211, 238, 0.14) 0%, rgba(20, 184, 166, 0.08) 100%)'
        : props.$isActive
        ? 'linear-gradient(90deg, rgba(34, 211, 238, 0.28) 0%, rgba(20, 184, 166, 0.18) 100%)'
        : 'transparent'};
    color: ${(props) => (!props.$isDisabled ? '#ffffff' : 'rgba(255, 255, 255, 0.3)')};
    /* Avoid horizontal translation to prevent perceived jiggle */
    transform: none;
  }

  svg {
    width: ${(props) => (props.$isCollapsed ? '24px' : '20px')};
    height: ${(props) => (props.$isCollapsed ? '24px' : '20px')};
    flex-shrink: 0;
    opacity: ${(props) => (props.$isActive ? '1' : '0.75')};
    transition: all 0.25s ease;
    filter: ${(props) =>
      props.$isActive ? 'drop-shadow(0 0 4px rgba(34, 211, 238, 0.5))' : 'none'};
  }
`

const NavItemText = styled.span<{ $isCollapsed: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  display: ${(props) => (props.$isCollapsed ? 'none' : 'inline')};
  transition: all 0.3s ease;
`

const UserSection = styled.div<{ $isCollapsed: boolean }>`
  padding: 1rem;
  border-top: 1px solid rgba(34, 211, 238, 0.15);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: fixed;
  left: 0;
  bottom: 0;
  width: ${(props) => (props.$isCollapsed ? '80px' : '280px')};
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.06) 0%, transparent 100%);
  border-radius: 0;

  &:hover {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.12) 0%, rgba(20, 184, 166, 0.06) 100%);
  }
`

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(34, 211, 238, 0.35);
  border: 2px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`

const UserInfo = styled.div<{ $isCollapsed: boolean }>`
  flex: 1;
  min-width: 0;
  opacity: ${(props) => (props.$isCollapsed ? '0' : '1')};
  visibility: ${(props) => (props.$isCollapsed ? 'hidden' : 'visible')};
  transition: all 0.3s ease;
`

const UserName = styled.div`
  color: #ffffff !important;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`

const UserRole = styled.div`
  color: rgba(255, 255, 255, 0.8) !important;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const getIconForMainNav = (item: string) => {
  switch (item) {
    case 'Overview':
      return <LayoutDashboard size={20} />
    case 'Datasets':
      return <Database size={20} />
    case 'Models':
      return <Brain size={20} />
    case 'Deployments':
      return <Rocket size={20} />
    case 'Compare':
      return <Scale size={20} />
    case 'Labeling':
      return <Tags size={20} />
    case 'Training':
      return <Zap size={20} />
    case 'Tools':
      return <Wrench size={20} />
    case 'Export':
      return <FileSpreadsheet size={20} />
    case 'Admin Dashboard':
      return <Building2 size={20} />
    // Project-specific navigation
    case 'All Projects':
      return <Building2 size={20} />
    default:
      return <Wrench size={20} />
  }
}

const UnifiedSidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [hoverTip, setHoverTip] = useState<{ visible: boolean; text: string; top: number }>({
    visible: false,
    text: '',
    top: 0,
  })
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const [showInsufficientDataDialog, setShowInsufficientDataDialog] = useState(false)
  const [showSelectProjectModal, setShowSelectProjectModal] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { logoutAndNavigate } = useAuth()

  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  const { data: user } = useFetchUserProfileQuery()
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )
  // Clear selected model when on models page (no active model selection needed)
  // Temporarily commented out to fix hot reload issue
  // useEffect(() => {
  //   if (location.pathname.startsWith('/models') && selectedModelId && selectedProjectId) {
  //     dispatch(
  //       setSelectedModelId({
  //         modelId: undefined,
  //         projectId: selectedProjectId,
  //       })
  //     )
  //   }
  // }, [location.pathname, selectedModelId, selectedProjectId, dispatch])

  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    undefined
  )
  const { data: brandingBlob } = useGetOrganizationBrandingPublicQuery()

  const [logoUrl, setLogoUrl] = useState<string>(branding.assets.logoSmall)

  // Update logo URL when organization branding changes
  useEffect(() => {
    if (brandingBlob) {
      const url = URL.createObjectURL(brandingBlob)
      setLogoUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setLogoUrl(branding.assets.logoSmall)
    }
  }, [brandingBlob])

  // Update global CSS class for layout adjustments
  useEffect(() => {
    const root = document.documentElement
    if (isCollapsed) {
      root.classList.add('sidebar-collapsed')
      root.classList.remove('sidebar-expanded')
    } else {
      root.classList.add('sidebar-expanded')
      root.classList.remove('sidebar-collapsed')
    }

    return () => {
      root.classList.remove('sidebar-collapsed', 'sidebar-expanded')
    }
  }, [isCollapsed])

  const currentSection = location.pathname.split('/')[1]
  const models = modelsData?.models || []
  const modelsCount = models.length
  const datasetsCount = Object.keys(
    (annoStats?.statistics?.datasource_distributions as Record<string, unknown>) || {}
  ).length
  const deploymentsCount = models.filter(
    (m) => m.deployment?.status?.toLowerCase() === 'in_service'
  ).length

  // Project-specific navigation items (when no project is selected)
  const projectNavItems = [{ key: 'projects', label: 'All Projects', path: '/projects' }]

  // Main navigation items (when project is selected)
  const mainNavItems = [
    { key: 'overview', label: 'Overview', path: `/overview/${selectedProjectId}` },
    { key: 'datasets', label: 'Datasets', path: `/datasets/${selectedProjectId}/all` },
    { key: 'labeling', label: 'Labeling', path: `/labeling/${selectedProjectId}` },
    { key: 'models', label: 'Models', path: `/models/${selectedProjectId}/overview` },
    { key: 'training', label: 'Training', path: `/training/${selectedProjectId}` },
    { key: 'deployment', label: 'Deployments', path: `/deployment/${selectedProjectId}` },
    { key: 'tools', label: 'Tools', path: `/tools/${selectedProjectId}` },
    { key: 'export', label: 'Export', path: `/export/${selectedProjectId}` },
  ]

  // Determine which navigation items to show
  // Treat profile routes the same as projects page (no project selected)
  const isProjectsPage = currentSection === 'projects' || currentSection === 'profile'
  const navigationItems = isProjectsPage ? projectNavItems : mainNavItems

  // Admin navigation items removed - now handled in Profile page
  // const adminNavItems: Array<{ key: string; label: string; path: string }> = []

  const handleMainNavClick = (item: { key: string; label: string; path: string }) => {
    if (!selectedProjectId && item.key !== 'admin-dashboard') {
      navigate('/projects')
      return
    }

    // Check for insufficient data for training
    if (item.key === 'training' && annoStats?.statistics) {
      const hasEnoughData =
        annoStats.statistics.total_annotations >= 10 &&
        Object.keys(annoStats.statistics.annotations_per_class || {}).filter(
          (key) => key !== 'Unknown' && key !== 'null'
        ).length >= 1
      if (!hasEnoughData) {
        setShowInsufficientDataDialog(true)
        return
      }
    }

    // Check for models requirement for tools
    if (item.key === 'tools' && models.length === 0) {
      return
    }

    navigate(item.path)
  }

  return (
    <>
      <SidebarContainer $isCollapsed={isCollapsed} ref={sidebarRef}>
        <SidebarHeader $isCollapsed={isCollapsed}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Logo
              src={logoUrl}
              alt={`${branding.company.name} Logo`}
              $isCollapsed={isCollapsed}
              onClick={() => navigate('/projects')}
              style={{ cursor: 'pointer' }}
            />
            <BrandText $isCollapsed={isCollapsed}>{branding.company.name}</BrandText>
          </div>
        </SidebarHeader>

        {/* Project Selector */}
        <ProjectSelector
          $isCollapsed={isCollapsed}
          onClick={() => (isProjectsPage ? null : setShowSelectProjectModal(true))}
        >
          {!isCollapsed ? (
            <>
              {isProjectsPage ? (
                <>
                  <div className="project-label">
                    <Building2 size={16} />
                    <span>Account</span>
                  </div>
                  <h3 className="project-name">{user?.first_name || 'User'}</h3>
                </>
              ) : (
                <>
                  <div className="project-label">
                    <Folder size={16} />
                    <span>Current Project</span>
                  </div>
                  <h3 className="project-name">{project?.name || 'Select Project'}</h3>
                </>
              )}
            </>
          ) : isProjectsPage ? (
            <Building2 size={24} />
          ) : (
            <Folder size={24} />
          )}
        </ProjectSelector>

        <SidebarContent>
          {/* Main Navigation */}
          <NavSection>
            <SectionTitle $isCollapsed={isCollapsed}>
              {isProjectsPage ? 'Projects' : 'Navigation'}
            </SectionTitle>
            {navigationItems.map((item) => {
              const isActive = isProjectsPage
                ? location.pathname === item.path
                : location.pathname.includes(`/${item.key}/`) ||
                  (item.key === 'overview' && location.pathname.includes('/overview/'))
              const isDisabled =
                !isProjectsPage &&
                ((item.key === 'training' && models.length === 0) ||
                  (item.key === 'tools' && models.length === 0))

              const counts: Record<string, number> = {
                models: modelsCount,
                datasets: datasetsCount,
                deployment: deploymentsCount,
              }

              return (
                <NavItem
                  key={item.key}
                  $isActive={isActive}
                  $isCollapsed={isCollapsed}
                  $isDisabled={isDisabled}
                  title={item.label}
                  aria-label={item.label}
                  onMouseEnter={(e) => {
                    if (!isCollapsed) return
                    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
                    const top = rect.top + rect.height / 2
                    ;(window as any).__av_side_nav_tip__ = { visible: true, text: item.label, top }
                    // trigger rerender via state setter below
                    setHoverTip({ visible: true, text: item.label, top })
                  }}
                  onMouseLeave={() => {
                    if (!isCollapsed) return
                    setHoverTip({ visible: false, text: '', top: 0 })
                  }}
                  onClick={() => !isDisabled && handleMainNavClick(item)}
                >
                  {getIconForMainNav(item.label)}
                  <NavItemText $isCollapsed={isCollapsed}>{item.label}</NavItemText>
                  {!isCollapsed && counts[item.key] != null && counts[item.key] > 0 && (
                    <Badge
                      bg="primary"
                      pill
                      style={{
                        marginLeft: 'auto',
                        background: 'linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 2px 6px rgba(34, 211, 238, 0.35)',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        padding: '2px 8px',
                      }}
                    >
                      {counts[item.key]}
                    </Badge>
                  )}
                </NavItem>
              )
            })}
          </NavSection>

          {/* Help Section */}
          <NavSection>
            <NavItem
              $isActive={false}
              $isCollapsed={isCollapsed}
              onClick={() => window.open('/docs/', '_blank')}
            >
              <HelpCircle size={20} />
              <NavItemText $isCollapsed={isCollapsed}>Documentation</NavItemText>
            </NavItem>
          </NavSection>
        </SidebarContent>

        {/* User Section - fixed at bottom of sidebar, outside scroll area */}
        <UserSection
          $isCollapsed={isCollapsed}
          onClick={() => !isCollapsed && navigate('/profile')}
          style={{ cursor: isCollapsed ? 'default' : 'pointer' }}
        >
          <UserAvatar>
            {user?.profile_400 || user?.profile_200 || user?.profile_100 ? (
              <img
                src={`data:image/;base64,${
                  user.profile_400 || user.profile_200 || user.profile_100
                }`}
                alt={`${user?.first_name || 'User'}'s profile`}
              />
            ) : (
              user?.first_name?.[0] || <User size={20} />
            )}
          </UserAvatar>
          <UserInfo $isCollapsed={isCollapsed}>
            <UserName>{user?.first_name || 'User'}</UserName>
            <UserRole>{user?.username || 'user'}</UserRole>
          </UserInfo>
          <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
            <Dropdown.Toggle
              as="div"
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                opacity: isCollapsed ? 0 : 1,
                visibility: isCollapsed ? 'hidden' : 'visible',
                transition: 'all 0.3s ease',
              }}
            >
              <Settings size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu variant="dark">
              <Dropdown.Item onClick={() => navigate('/profile')}>
                <User size={16} style={{ marginRight: '8px' }} />
                Profile{user?.username === 'admin' ? ' / Admin' : ''}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={logoutAndNavigate}>
                <LogOut size={16} style={{ marginRight: '8px' }} />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </UserSection>
        {isCollapsed &&
          hoverTip.visible &&
          typeof document !== 'undefined' &&
          ReactDOM.createPortal(
            <TooltipBubble style={{ top: hoverTip.top }}>{hoverTip.text}</TooltipBubble>,
            document.body
          )}
      </SidebarContainer>

      {/* Toggle button outside sidebar container to avoid clipping */}
      <button
        type="button"
        aria-label="Toggle sidebar"
        className="btn btn-primary p-0 rounded-circle d-flex align-items-center justify-content-center position-fixed"
        style={{
          left: isCollapsed ? 80 : 280,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 28,
          height: 28,
          background: 'linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%)',
          border: '2px solid rgba(255, 255, 255, 0.25)',
          boxShadow: '0 4px 12px rgba(34, 211, 238, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          zIndex: 1001,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)'
          e.currentTarget.style.boxShadow =
            '0 6px 16px rgba(34, 211, 238, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'
          e.currentTarget.style.boxShadow =
            '0 4px 12px rgba(34, 211, 238, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      <ConfirmDialog
        show={showInsufficientDataDialog}
        onHide={() => setShowInsufficientDataDialog(false)}
        onConfirmRight={() => setShowInsufficientDataDialog(false)}
        title="Insufficient Data for Models"
        message={
          <div>
            <p>You need more data before you can access the Models section.</p>
            <p>
              <strong>OBB and BBox Requirements:</strong>
            </p>
            <ul>
              <li>
                At least <strong>10 annotations</strong>
              </li>
              <li>
                At least <strong>1 class</strong> (excluding 'Unknown' or 'null')
              </li>
            </ul>
            <p>
              <strong>Classification Requirements:</strong>
            </p>
            <ul>
              <li>
                At least <strong>10 annotations</strong>
              </li>
              <li>
                At least <strong>2 classes</strong>
              </li>
            </ul>
            <p>Please add more annotations to your dataset first.</p>
          </div>
        }
        confirmRightText="OK"
        variant="warning"
      />

      <SelectProjectModal
        show={showSelectProjectModal}
        onHide={() => setShowSelectProjectModal(false)}
      />
    </>
  )
}

export default UnifiedSidebar
